import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { contentApi } from '@/services/adminApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save } from 'lucide-react';
import type { Content } from '@/types/admin';

const contentSchema = z.object({
  contentId: z.string().max(255, 'Content ID must be less than 255 characters').optional(),
  title: z.string().min(1, 'Title is required').max(255, 'Title must be less than 255 characters'),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  type: z.enum(['IMAGE', 'VIDEO', 'DOCUMENT', 'AUDIO', 'TEXT', 'OTHER']),
  contentFor: z.enum(['USER_GENERATED', 'SYSTEM_GENERATED', 'ADMIN_GENERATED']).optional(),
  contentFormat: z.enum([
    'IMAGE_JPG',
    'IMAGE_JPEG',
    'IMAGE_PNG',
    'IMAGE_GIF',
    'VIDEO_MP4',
    'VIDEO_MOV',
    'VIDEO_AVI',
    'DOCUMENT_PDF',
    'DOCUMENT_DOCX',
    'DOCUMENT_TXT',
    'DOCUMENT_XLSX',
    'AUDIO_MP3',
    'AUDIO_WAV',
    'DEFAULT',
  ]).optional(),
  category: z.string().optional(),
  tags: z.string().optional(), // Comma-separated tags
  featured: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED', 'DELETED']).optional(),
  metadataJson: z.string().optional().refine((value) => {
    if (!value || !value.trim()) return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }, { message: 'Metadata must be valid JSON' }),
});

type ContentFormData = z.infer<typeof contentSchema>;

export const ContentFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [file, setFile] = useState<File | null>(null);

  const form = useForm<ContentFormData>({
    resolver: zodResolver(contentSchema),
    defaultValues: {
      contentId: '',
      title: '',
      description: '',
      type: 'TEXT',
      contentFor: undefined,
      contentFormat: undefined,
      category: '',
      tags: '',
      featured: false,
      status: 'DRAFT',
      metadataJson: '',
    },
  });

  // Fetch content data for edit mode
  const {
    data: content,
    isLoading: isLoadingContent,
    error: contentError,
  } = useEntity<Content>({
    fetchFn: (contentId: string) => contentApi.getById(contentId),
    entityId: id,
    entityName: 'content',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        contentId: data.contentId || '',
        title: data.title,
        description: data.description || '',
        type: data.type,
        contentFor: data.contentFor,
        contentFormat: data.contentFormat,
        category: data.category || '',
        tags: data.tags?.join(', ') || '',
        featured: data.featured || false,
        status: data.status,
        metadataJson: data.metadata ? JSON.stringify(data.metadata, null, 2) : '',
      });
    },
    onError: () => {
      navigate('/admin/contents');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ContentFormData) => {
      if (!file) {
        throw new Error('File is required for new content');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', data.title);
      if (data.description) formData.append('description', data.description);
      formData.append('type', data.type);
      if (data.category) formData.append('category', data.category);
      if (data.tags) {
        const tags = data.tags.split(',').map((t) => t.trim()).filter(Boolean);
        tags.forEach((tag) => formData.append('tags', tag));
      }
      formData.append('featured', data.featured.toString());
      formData.append('status', data.status || 'DRAFT');

      return await contentApi.create(formData);
    },
    entityName: 'content',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/contents');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ContentFormData) => {
      if (!id) throw new Error('Content ID is required');
      const metadata =
        data.metadataJson && data.metadataJson.trim()
          ? (JSON.parse(data.metadataJson) as Record<string, unknown>)
          : undefined;
      return await contentApi.update(id, {
        contentId: data.contentId || undefined,
        title: data.title,
        description: data.description,
        type: data.type,
        contentFor: data.contentFor,
        contentFormat: data.contentFormat,
        category: data.category,
        tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        featured: data.featured,
        status: data.status,
        metadata,
      });
    },
    entityName: 'content',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/contents');
    },
  });

  const onSubmit = async (data: ContentFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingContent;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading content..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && contentError) {
    return <ErrorState error={contentError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/contents">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Content' : 'Create Content'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update content information' : 'Upload and configure new content'}
            </p>
            {isEdit && id && (
              <div className="mt-3">
                <CopyableId id={id} label="Record ID" />
              </div>
            )}
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the content</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEdit && (
                <FormField
                  control={form.control}
                  name="contentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content ID</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter business content ID" {...field} />
                      </FormControl>
                      <FormDescription>Business identifier (e.g., CNT-...)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {!isEdit && (
                <div className="space-y-2">
                  <Label htmlFor="file">File *</Label>
                  <Input
                    id="file"
                    type="file"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    required={!isEdit}
                  />
                  <p className="text-sm text-muted-foreground">
                    Upload a file for this content. Required for new content.
                  </p>
                </div>
              )}

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter content title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter content description"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Maximum 1000 characters</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select content type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="IMAGE">Image</SelectItem>
                        <SelectItem value="VIDEO">Video</SelectItem>
                        <SelectItem value="DOCUMENT">Document</SelectItem>
                        <SelectItem value="AUDIO">Audio</SelectItem>
                        <SelectItem value="TEXT">Text</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isEdit && (
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="contentFor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content For</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select content source" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="USER_GENERATED">User Generated</SelectItem>
                            <SelectItem value="SYSTEM_GENERATED">System Generated</SelectItem>
                            <SelectItem value="ADMIN_GENERATED">Admin Generated</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contentFormat"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Content Format</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select content format" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="DEFAULT">DEFAULT</SelectItem>
                            <SelectItem value="DOCUMENT_TXT">DOCUMENT_TXT</SelectItem>
                            <SelectItem value="DOCUMENT_PDF">DOCUMENT_PDF</SelectItem>
                            <SelectItem value="DOCUMENT_DOCX">DOCUMENT_DOCX</SelectItem>
                            <SelectItem value="DOCUMENT_XLSX">DOCUMENT_XLSX</SelectItem>
                            <SelectItem value="IMAGE_PNG">IMAGE_PNG</SelectItem>
                            <SelectItem value="IMAGE_JPG">IMAGE_JPG</SelectItem>
                            <SelectItem value="IMAGE_JPEG">IMAGE_JPEG</SelectItem>
                            <SelectItem value="IMAGE_GIF">IMAGE_GIF</SelectItem>
                            <SelectItem value="VIDEO_MP4">VIDEO_MP4</SelectItem>
                            <SelectItem value="VIDEO_MOV">VIDEO_MOV</SelectItem>
                            <SelectItem value="VIDEO_AVI">VIDEO_AVI</SelectItem>
                            <SelectItem value="AUDIO_MP3">AUDIO_MP3</SelectItem>
                            <SelectItem value="AUDIO_WAV">AUDIO_WAV</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter category" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter tags separated by commas"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="featured"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Featured</FormLabel>
                      <FormDescription>Mark this content as featured</FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {isEdit && (
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="DRAFT">Draft</SelectItem>
                          <SelectItem value="PUBLISHED">Published</SelectItem>
                          <SelectItem value="ARCHIVED">Archived</SelectItem>
                          <SelectItem value="DELETED">Deleted</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isEdit && (
                <FormField
                  control={form.control}
                  name="metadataJson"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Metadata (JSON)</FormLabel>
                      <FormControl>
                        <textarea
                          className="flex min-h-[220px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                          placeholder={`{\n  "key": "value"\n}`}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Edit structured metadata (must be valid JSON). Leave empty to clear.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </CardContent>
          </Card>

          {isEdit && content && (
            <Card>
              <CardHeader>
                <CardTitle>File Information (read-only)</CardTitle>
                <CardDescription>Stored file metadata for this content</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                {content.fileName && (
                  <div>
                    <Label>File Name</Label>
                    <p className="mt-1 text-sm">{content.fileName}</p>
                  </div>
                )}
                {content.mimeType && (
                  <div>
                    <Label>MIME Type</Label>
                    <p className="mt-1 text-sm">{content.mimeType}</p>
                  </div>
                )}
                {content.fileSize !== undefined && (
                  <div>
                    <Label>File Size (bytes)</Label>
                    <p className="mt-1 text-sm font-mono">{content.fileSize}</p>
                  </div>
                )}
                {content.cloudProvider && (
                  <div>
                    <Label>Cloud Provider</Label>
                    <p className="mt-1 text-sm">{content.cloudProvider}</p>
                  </div>
                )}
                {content.fileUrl && (
                  <div className="md:col-span-2">
                    <Label>File URL</Label>
                    <p className="mt-1 text-sm break-all">
                      <a
                        className="text-primary hover:underline"
                        href={content.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {content.fileUrl}
                      </a>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/contents">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ContentFormPage;


